import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Forward, Plus, Trash2, Save, Loader2, RefreshCw, Inbox, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("hu-HU", { timeZone: "Europe/Budapest", hour12: false }) : "—");

export function ModForwarding() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [polling, setPolling] = useState(false);
  useEffect(() => { api.get("/admin/forwarding").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="grid h-40 place-items-center"><Loader2 className="animate-spin text-zinc-600" /></div>;

  const setRule = (i, k, v) => setS({ ...s, rules: s.rules.map((r, j) => (j === i ? { ...r, [k]: v } : r)) });
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/admin/forwarding", { imap_user: s.imap_user, imap_host: s.imap_host, rules: s.rules });
      setS(r.data);
      toast.success("Továbbítási beállítások mentve");
    } catch (e) {
      toast.error(apiErr(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  const poll = async () => {
    setPolling(true);
    try { const r = await api.post("/admin/forwarding/poll"); setS(r.data); toast.success("Postafiók lekérdezve"); } finally { setPolling(false); }
  };
  const inp = "h-9 border-zinc-800 bg-zinc-950 text-[13px] text-zinc-100";

  return (
    <div className="mx-auto max-w-4xl space-y-5" data-testid="mod-forwarding">
      <div>
        <h2 className="flex items-center gap-2 text-lg font-semibold text-zinc-50"><Forward size={18} className="text-blue-400" /> Report továbbító</h2>
        <p className="mt-1 text-[12px] text-zinc-500">Az új bejelentések automatikusan a platformhoz rendelt címre mennek, „Content Report” feladónévvel. A levélben csak a leírás és az URL szerepel, DSA-jelzés nélkül. A platform válaszai az ügynél jelennek meg.</p>
      </div>

      <section className="rounded-xl border border-zinc-800/70 bg-[#111113] p-4">
        <div className="mb-3 flex items-center gap-2 text-[13px] font-medium text-zinc-200"><Inbox size={15} className="text-blue-400" /> Válaszok fogadása (IMAP postafiók)</div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input data-testid="fwd-imap-user" value={s.imap_user || ""} onChange={(e) => setS({ ...s, imap_user: e.target.value })} placeholder="postafiok@gmail.com" className={inp} />
          <Input data-testid="fwd-imap-host" value={s.imap_host || ""} onChange={(e) => setS({ ...s, imap_host: e.target.value })} placeholder="imap.gmail.com" className={inp} />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3 text-[12px]">
          <span className={s.password_configured ? "text-emerald-400" : "text-amber-400"} data-testid="fwd-password-status">
            {s.password_configured ? <CheckCircle2 size={13} className="mr-1 inline" /> : <AlertTriangle size={13} className="mr-1 inline" />}
            Alkalmazásjelszó {s.password_configured ? "beállítva (szerveren tárolva)" : "hiányzik"}
          </span>
          <span className="text-zinc-500" data-testid="fwd-last-poll">Utolsó lekérdezés: {fmt(s.last_poll)}</span>
          {s.last_error && <span className="text-red-400" data-testid="fwd-last-error">Hiba: {s.last_error}</span>}
          {!s.imap_user && <span className="w-full rounded-md border border-red-500/40 bg-red-500/10 px-2.5 py-1.5 text-red-300" data-testid="fwd-no-mailbox">A válaszcím (postafiók címe) nincs megadva — amíg nincs, a rendszer nem továbbít, különben a platformok válaszai nem érkeznének meg.</span>}
          <button data-testid="fwd-poll-now" onClick={poll} disabled={polling || !s.imap_user} className="ml-auto inline-flex items-center gap-1 rounded-md border border-zinc-800 px-2.5 py-1.5 text-zinc-300 hover:bg-zinc-900 disabled:opacity-40">
            <RefreshCw size={12} className={polling ? "animate-spin" : ""} /> Lekérdezés most
          </button>
        </div>
      </section>

      <section className="rounded-xl border border-zinc-800/70 bg-[#111113] p-4">
        <div className="mb-3 text-[13px] font-medium text-zinc-200">Platform → e-mail cím</div>
        <div className="space-y-2">
          {s.rules.length === 0 && <p className="text-[12px] text-zinc-600">Még nincs szabály.</p>}
          {s.rules.map((r, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`fwd-rule-${i}`}>
              <Input data-testid={`fwd-rule-platform-${i}`} value={r.platform} onChange={(e) => setRule(i, "platform", e.target.value)} placeholder="Platform (pl. Roblox)" className={`${inp} w-48`} />
              <Input data-testid={`fwd-rule-email-${i}`} value={r.email} onChange={(e) => setRule(i, "email", e.target.value)} placeholder="safety@platform.com" className={inp} />
              <Switch data-testid={`fwd-rule-active-${i}`} checked={r.active} onCheckedChange={(v) => setRule(i, "active", v)} />
              <button data-testid={`fwd-rule-remove-${i}`} onClick={() => setS({ ...s, rules: s.rules.filter((_, j) => j !== i) })} className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-red-400/70 hover:bg-red-950/40"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <button data-testid="fwd-rule-add" onClick={() => setS({ ...s, rules: [...s.rules, { platform: "", email: "", active: true }] })} className="inline-flex h-9 items-center gap-1 rounded-md border border-zinc-800 px-3 text-[12px] text-zinc-300 hover:bg-zinc-900"><Plus size={13} /> Szabály</button>
          <button data-testid="fwd-save" onClick={save} disabled={saving} className="ml-auto inline-flex h-9 items-center gap-2 rounded-md bg-blue-600 px-5 text-[13px] font-semibold text-white hover:bg-blue-500 disabled:opacity-40">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Mentés
          </button>
        </div>
      </section>
      <Unmatched />
    </div>
  );
}

function Unmatched() {
  const [items, setItems] = useState([]);
  const [refs, setRefs] = useState({});
  const load = () => api.get("/admin/forwarding/unmatched").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const assign = async (m) => {
    try { await api.post(`/admin/forwarding/unmatched/${m.id}/assign`, { reference: refs[m.id] || "" }); toast.success("Hozzárendelve — a ticket szám alapján a következő válaszok automatikusan ide kerülnek"); load(); }
    catch (e) { toast.error(apiErr(e?.response?.data?.detail)); }
  };
  const dismiss = async (m) => { await api.delete(`/admin/forwarding/unmatched/${m.id}`); load(); };
  return (
    <section className="rounded-xl border border-amber-500/25 bg-[#111113] p-4" data-testid="fwd-unmatched">
      <div className="mb-1 text-[13px] font-medium text-zinc-200">Hozzárendeletlen platform-levelek ({items.length})</div>
      <p className="mb-3 text-[11px] text-zinc-500">Pl. „Roblox Support Ticket #1234567” automatikus visszaigazolás, amelyben nincs benne a mi azonosítónk. Rendeld hozzá egyszer az ügyhöz: a rendszer megjegyzi a ticket számot, és a további válaszokat már magától oda csatolja.</p>
      {items.length === 0 && <p className="text-[12px] text-zinc-600">Nincs ilyen levél.</p>}
      <div className="space-y-2">
        {items.map((m) => (
          <div key={m.id} className="rounded-lg border border-zinc-800 bg-zinc-950 p-3" data-testid={`fwd-unmatched-${m.id}`}>
            <div className="flex items-center justify-between gap-2 text-[11px]"><span className="truncate text-amber-300">{m.from}</span><span className="text-zinc-500">{fmt(m.received_at)}</span></div>
            <div className="mt-0.5 text-[13px] font-medium text-zinc-100">{m.subject}</div>
            <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-[12px] text-zinc-400">{m.text}</p>
            <div className="mt-2 flex gap-2">
              <Input data-testid={`fwd-unmatched-ref-${m.id}`} value={refs[m.id] || ""} onChange={(e) => setRefs({ ...refs, [m.id]: e.target.value })} placeholder="DSA-2026-…" className="h-8 w-56 border-zinc-800 bg-black font-mono text-[12px]" />
              <button data-testid={`fwd-unmatched-assign-${m.id}`} onClick={() => assign(m)} disabled={!refs[m.id]} className="h-8 rounded-md bg-blue-600 px-3 text-[12px] font-semibold text-white disabled:opacity-40">Hozzárendelés</button>
              <button data-testid={`fwd-unmatched-dismiss-${m.id}`} onClick={() => dismiss(m)} className="ml-auto h-8 rounded-md px-2 text-[12px] text-zinc-500 hover:text-red-300">Elvetés</button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function PlatformReplies({ report, onChanged }) {
  const [busy, setBusy] = useState(false);
  const f = report.forward;
  const replies = report.platform_replies || [];
  useEffect(() => {
    if (report.has_new_platform_reply) api.patch(`/mod/reports/${report.reference}/platform-replies/seen`).catch(() => {});
  }, [report.reference, report.has_new_platform_reply]);
  const forward = async () => {
    setBusy(true);
    try { await api.post(`/mod/reports/${report.reference}/forward`, {}); toast.success("Továbbítva a platformnak"); onChanged?.(); }
    catch (e) { toast.error(apiErr(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="platform-forward">
      <div className="flex flex-wrap items-center gap-2 text-[12px]">
        {f ? (
          <span className={f.status === "sent" ? "text-emerald-400" : "text-red-400"} data-testid="platform-forward-status">
            #{f.code} → {f.to} · {f.status === "sent" ? "elküldve" : "sikertelen"} · {fmt(f.sent_at)}{f.attachments ? ` · ${f.attachments} melléklet` : ""}{f.reply_to ? ` · válaszcím: ${f.reply_to}` : ""}{f.ticket_ids?.length ? ` · platform ticket: ${f.ticket_ids.map((t) => `#${t}`).join(", ")}` : ""}
          </span>
        ) : <span className="text-zinc-500">Nincs továbbítva</span>}
        <button data-testid="platform-forward-btn" onClick={forward} disabled={busy} className="ml-auto inline-flex items-center gap-1 rounded-md border border-zinc-800 px-2.5 py-1 text-zinc-300 hover:bg-zinc-900 disabled:opacity-40">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Forward size={12} />} {f ? "Újraküldés" : "Továbbítás most"}
        </button>
      </div>
      {replies.map((m) => (
        <div key={m.id} className="rounded-lg border border-emerald-500/25 bg-emerald-500/5 p-3" data-testid={`platform-reply-${m.id}`}>
          <div className="mb-1 flex items-center justify-between gap-2 text-[11px]">
            <span className="truncate font-medium text-emerald-300">{m.from}</span>
            <span className="shrink-0 text-zinc-500">{fmt(m.received_at)}</span>
          </div>
          <div className="mb-1 text-[11px] text-zinc-500">{m.subject}</div>
          <p className="max-h-60 overflow-y-auto whitespace-pre-wrap text-[13px] leading-relaxed text-zinc-300">{m.text}</p>
        </div>
      ))}
    </div>
  );
}
